import { Component, EventEmitter, Input, OnInit, Output, NgZone } from '@angular/core';

declare const AppleID: any;

export interface AppleSignInSuccess {
  code: string;
  id_token: string;
  state?: string | null;
  user: {
    name: {
      firstName: string | null;
      lastName: string | null;
    };
    email: string | null;
  } | null;
}

export interface AppleSignInFailure {
  error: string;
  description?: string;
}

const AppleComponentResource = {
    appleButtonLabel: "Continue with Apple",
    signingInLabel: "Signing in with Apple..."
};

@Component({
  selector: 'app-apple-signin',
  standalone: false,
  templateUrl: './apple-signin.component.html',
  styleUrls: ['./apple-signin.component.scss']
})
export class AppleSigninComponent implements OnInit {
  // Input
  @Input() clientId!: string;
  @Input() showWarningAndErrors: boolean = true;

  // Output
  @Output() successCallBack = new EventEmitter<AppleSignInSuccess>();
  @Output() failureCallBack = new EventEmitter<AppleSignInFailure>();
  isLoading = false;
  appleResources = AppleComponentResource;

  // Internal State
  private isInitialized = false;

  constructor(private ngZone: NgZone) { }

  //  Lifecycle
  ngOnInit(): void {
    // Validate all required inputs upfront and throw a single descriptive error
    // if any are missing, rather than failing silently later during sign-in.
    const missing: string[] = [];
    if (!this.clientId) missing.push('clientId');

    if (missing.length > 0) {
      throw new Error(`[AppleSignIn] Missing required inputs: ${missing.join(', ')}`);
    }

    // Load the Apple JS SDK script dynamically, then initialize once ready.
    this.loadAppleScript()
      .then(() => this.initAppleSignIn())
      .catch((err) => {
        if (this.showWarningAndErrors) {
          console.error('[AppleSignIn] Failed to load Apple JS SDK:', err);
        }
        // Emit so the parent can show a user-facing message (e.g. "Check your connection")
        this.failureCallBack.emit({
          error: 'sdk_load_failed',
          description: 'Failed to load Apple JS SDK. Check your internet connection.'
        });
      });
  }

  //SDK Loading
  /**
   * Dynamically injects the Apple JS SDK script into the document head.
   * Handles three cases:
   *  1. SDK already available globally (AppleID defined) — resolve immediately.
   *  2. Script tag already in DOM but still loading — poll until AppleID is ready.
   *  3. Script not in DOM yet — create and append it, resolve on load.
   */
  private loadAppleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Case 1: SDK already loaded from a previous component init or page load
      if (typeof AppleID !== 'undefined') {
        resolve();
        return;
      }

      // Case 2: Script tag exists but hasn't finished executing yet
      if (document.querySelector('script[src*="appleid.auth.js"]')) {
        const interval = setInterval(() => {
          if (typeof AppleID !== 'undefined') {
            clearInterval(interval);
            resolve();
          }
        }, 100);
        return;
      }

      // Case 3: First load — inject script tag into <head>
      const script = document.createElement('script');
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Apple JS SDK'));
      document.head.appendChild(script);
    });
  }

  // Initialization
  /**
   * Calls AppleID.auth.init() with the component inputs.
   * Must be called after the SDK script has loaded.
   * usePopup: true means Apple handles the flow in a popup window
   * instead of redirecting the page — redirectURI is still required
   * by Apple even in popup mode, but no actual redirect occurs.
   */
  private initAppleSignIn(): void {
    try {
      AppleID.auth.init({
        clientId: this.clientId,
        scope: "name email",
        redirectURI: window.location.origin,        // Redirect URI still required for popup mode
        usePopup: true,        // Open In Popup Mode
      });
      this.isInitialized = true;
    } catch (err) {
      if (this.showWarningAndErrors) {
        console.error('[AppleSignIn] Init failed:', err);
      }
      // Likely a misconfiguration (wrong clientId, unregistered redirectURI, etc.)
      this.failureCallBack.emit({
        error: 'init_failed',
        description: 'Apple Sign-In SDK failed to initialize.'
      });
    }
  }

  // Sign-In Trigger
  /**
   * Called when the user clicks the Sign in with Apple button.
   * Opens the Apple popup and awaits the authorization response.
   *
   * If the SDK hasn't initialized yet (e.g. slow network), it retries
   * init once before giving up.
   */
  async signInWithApple(): Promise<void> {
    if (!this.isInitialized) {
      if (this.showWarningAndErrors) {
        console.warn('[AppleSignIn] SDK not initialized yet, retrying init...');
      }
      // Attempt a one-time retry in case the script loaded but init hadn't run yet
      this.initAppleSignIn();
      if (!this.isInitialized) return;
    }

    this.isLoading = true;

    try {
      const response = await AppleID.auth.signIn();
      // Run inside NgZone because AppleID resolves outside Angular's change detection
      this.ngZone.run(() => this.handleSuccess(response));
    } catch (error: any) {
      this.ngZone.run(() => {
        this.isLoading = false;
        // Forward the Apple error object directly using the typed interface.
        // Common error codes from Apple:
        //   'popup_closed_by_user'    — user closed the popup manually
        //   'user_cancelled_authorize' — user clicked Cancel inside the popup
        //   'invalid_client'           — clientId or redirectURI not recognised by Apple
        //   'invalid_request'          — malformed request
        //   'popup_blocked'            — browser blocked the popup
        this.failureCallBack.emit({
          error: error?.error ?? 'unknown_error',
          description: error?.description ?? undefined
        });

        if (this.showWarningAndErrors) {
          console.error('[AppleSignIn] Error:', error);
        }
      });
    }
  }
  // ── Success Handler ─────────────────────────────────────────────────────
  /**
   * Processes a successful Apple authorization response.
   * Extracts authCode and identityToken from response.authorization.
   * Extracts user info from response.user (only present on first-ever login).
   *
   * Apple's response shape:
   * {
   *   authorization: { code: string, id_token: string },
   *   user?: {                        ← undefined on all logins after the first
   *     name: { firstName, lastName },
   *     email: string
   *   }
   * }
   */
  private handleSuccess(response: any): void {
    console.log(response);
    this.isLoading = false;

    const code = response?.authorization?.code;
    const id_token = response?.authorization?.id_token;
    const state = response?.authorization?.state ?? null;

    // authCode is required — without it the backend cannot validate the sign-in
    if (!code) {
      this.failureCallBack.emit({
        error: 'no_auth_code',
        description: 'Apple responded but did not return an authorization code.'
      });
      return;
    }

    // user is only included on the very first sign-in ever for this Apple ID.
    // Store it immediately on your backend — Apple will never send it again.
    const user = response.user ? {
      name: {
        firstName: response.user.name?.firstName ?? null,
        lastName: response.user.name?.lastName ?? null,
      },
      email: response.user.email ?? null,
    } : null;

    this.successCallBack.emit({ code, id_token, state, user });
  }
}
